import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { t } from '@trimble/i18n';

export type CrsIssueKind = 'mismatch' | 'missing';

export interface CrsMismatchNotifier {
  reportMismatch(): void;
  reportMissing(): void;
  cancel(): void;
}

const DEFAULT_COALESCE_WINDOW_MS = 500;

/**
 * Coalesces CRS warnings so a burst of point-cloud loads emits at most one
 * toast per issue kind (mismatch vs missing CSIB).
 */
export function createCrsMismatchNotifier(
  coalesceWindowMs: number = DEFAULT_COALESCE_WINDOW_MS
): CrsMismatchNotifier {
  const pending = new Set<CrsIssueKind>();
  let timer: ReturnType<typeof setTimeout> | undefined;

  const flush = (): void => {
    timer = undefined;
    if (pending.has('mismatch')) {
      NotificationEventBus.emit({
        type: 'warning',
        message: t('mobileMapping.projectData.crsMismatch'),
      });
    }
    if (pending.has('missing')) {
      NotificationEventBus.emit({
        type: 'warning',
        message: t('mobileMapping.projectData.pointCloudMissingCrs'),
      });
    }
    pending.clear();
  };

  const report = (kind: CrsIssueKind): void => {
    pending.add(kind);
    if (timer !== undefined) {
      clearTimeout(timer);
    }
    timer = setTimeout(flush, coalesceWindowMs);
  };

  return {
    reportMismatch(): void {
      report('mismatch');
    },
    reportMissing(): void {
      report('missing');
    },
    cancel(): void {
      if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
      }
      pending.clear();
    },
  };
}
